import {
  useCallback,
  useEffect,
  useState
} from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent
} from '@/components/ui/dialog';
import {
  Package,
  Plus,
  Search,
  ShoppingCart,
  Star,
  Truck,
  Maximize2,
  X
} from 'lucide-react';
import {
  useNavigate
} from 'react-router-dom';

const fmt = (valor) =>
  Number(valor || 0).toLocaleString(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL'
    }
  );

export default function AlunoLojaPage() {
  const [produtos, setProdutos] =
    useState([]);
  const [loading, setLoading] =
    useState(true);
  const [search, setSearch] =
    useState('');

  const [imagemAberta, setImagemAberta] =
    useState(null);

  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(
        localStorage.getItem('sm_cart') ||
          '[]'
      );
    } catch {
      return [];
    }
  });

  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const resposta = await api.get(
        '/aluno/loja/produtos'
      );

      setProdutos(resposta.data || []);
    } catch {
      toast.error(
        'Erro ao carregar produtos.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    localStorage.setItem(
      'sm_cart',
      JSON.stringify(cart)
    );
  }, [cart]);

  const addToCart = (produto) => {
    if (
      Number(
        produto.estoque_disponivel || 0
      ) <= 0
    ) {
      toast.error(
        'Produto sem estoque disponível.'
      );
      return;
    }

    setCart((anterior) => {
      const existente = anterior.find(
        (item) =>
          item.produto_id === produto.id
      );

      if (existente) {
        if (
          existente.quantidade >=
          produto.estoque_disponivel
        ) {
          toast.error(
            'Você atingiu o estoque disponível.'
          );
          return anterior;
        }

        return anterior.map((item) =>
          item.produto_id === produto.id
            ? {
                ...item,
                quantidade:
                  item.quantidade + 1,
                estoque_disponivel:
                  produto.estoque_disponivel
              }
            : item
        );
      }

      return [
        ...anterior,
        {
          produto_id: produto.id,
          nome: produto.nome,
          preco: produto.preco,
          imagem_url:
            produto.imagem_url,
          quantidade: 1,
          estoque_disponivel:
            produto.estoque_disponivel,
          prazo_entrega_tipo:
            produto.prazo_entrega_tipo,
          prazo_entrega_dias:
            produto.prazo_entrega_dias,
          prazo_entrega_texto:
            produto.prazo_entrega_texto
        }
      ];
    });

    toast.success(
      `${produto.nome} adicionado ao carrinho.`
    );
  };

  const cartCount = cart.reduce(
    (total, item) =>
      total + item.quantidade,
    0
  );

  const termo = search.toLowerCase();

  const filtered = produtos.filter(
    (produto) =>
      produto.nome
        ?.toLowerCase()
        .includes(termo) ||
      produto.categoria
        ?.toLowerCase()
        .includes(termo) ||
      produto.descricao
        ?.toLowerCase()
        .includes(termo)
  );

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((item) => (
          <Skeleton
            key={item}
            className="h-72 bg-[#141414]"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] uppercase text-white">
          Loja
        </h1>

        <Button
          onClick={() =>
            navigate('/aluno/carrinho')
          }
          className="relative bg-[var(--cor-primaria,#007AFF)]"
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          Carrinho

          {cartCount > 0 && (
            <Badge className="absolute -top-2 -right-2 bg-red-500 text-white">
              {cartCount}
            </Badge>
          )}
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          className="pl-10 bg-[#0A0A0A] border-[#27272A]"
          placeholder="Buscar produtos..."
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
        />
      </div>

      {filtered.length === 0 ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-12 text-center text-zinc-500">
            Nenhum produto disponível.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((produto) => {
            const semEstoque =
              produto.estoque_disponivel <= 0;

            const noCarrinho = cart.find(
              (item) =>
                item.produto_id === produto.id
            );

            return (
              <Card
                key={produto.id}
                className="bg-[#141414] border-[#27272A] overflow-hidden relative"
              >
                {produto.destaque && (
                  <Badge className="absolute z-10 top-2 right-2 bg-amber-500 text-black border-0">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    Destaque
                  </Badge>
                )}

                <div className="aspect-square bg-[#0A0A0A] relative group">
                  {produto.imagem_url ? (
                    <button
                      type="button"
                      onClick={() =>
                        setImagemAberta({
                          url: produto.imagem_url,
                          nome: produto.nome
                        })
                      }
                      className="w-full h-full relative cursor-zoom-in"
                    >
                      <img
                        src={produto.imagem_url}
                        alt={produto.nome}
                        className="w-full h-full object-cover"
                      />

                      <div className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black/70 flex items-center justify-center">
                        <Maximize2 className="w-4 h-4 text-white" />
                      </div>
                    </button>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-12 h-12 text-zinc-700" />
                    </div>
                  )}
                </div>

                <CardContent className="p-3 space-y-2">
                  <p className="text-sm font-medium text-white">
                    {produto.nome}
                  </p>

                  <p className="text-lg font-black text-white">
                    {fmt(produto.preco)}
                  </p>

                  <div className="flex items-center gap-1 text-xs text-zinc-400">
                    <Truck className="w-3.5 h-3.5 text-[#007AFF]" />
                    {produto.prazo_entrega_texto}
                  </div>

                  <p className={
                    semEstoque
                      ? 'text-xs text-red-400'
                      : 'text-xs text-zinc-500'
                  }>
                    {semEstoque
                      ? 'Sem estoque'
                      : `${produto.estoque_disponivel} disponível`}
                  </p>

                  <Button
                    size="sm"
                    disabled={semEstoque}
                    onClick={() =>
                      addToCart(produto)
                    }
                    className="w-full bg-[var(--cor-primaria,#007AFF)]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {noCarrinho
                      ? noCarrinho.quantidade
                      : 'Adicionar'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <Dialog
        open={Boolean(imagemAberta)}
        onOpenChange={(aberto) => {
          if (!aberto) setImagemAberta(null);
        }}
      >
        <DialogContent className="max-w-none w-screen h-screen p-0 bg-black/95 border-0 rounded-none flex items-center justify-center">
          {imagemAberta && (
            <div className="relative w-full h-full flex items-center justify-center p-4 sm:p-8">
              <button
                type="button"
                onClick={() => setImagemAberta(null)}
                className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <img
                src={imagemAberta.url}
                alt={imagemAberta.nome}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>

  );
}
